/**
 * Atlas PM · 任务状态机
 *
 * 这是跨会话协作的**核心契约之一**：P1-B（审计与冻结）、P2（业务）都依赖它。
 * 定义冻结后不得随意变更，需走 ops/变更申请.md。
 *
 * 状态流转图（详见方案文档 20-权限审批与数据锁定设计.md）：
 *
 *   DRAFT ──提交──> SUBMITTED ──进入审批──> APPROVING ──通过──> FROZEN
 *                                              │
 *                                              ├─驳回─> REJECTED ──修改──> DRAFT
 *                                              │
 *                                              └─终止超时审批(P6.16)─> IN_PROGRESS
 *
 *   FROZEN ──管理员申请解冻──> UNLOCKED_EDITING ──超时72h──> FROZEN（自动回冻）
 *                                      │
 *                                      └─重新报审──> SUBMITTED
 *
 *   任意态 ──取消──> CANCELLED
 *   FROZEN ──归档──> ARCHIVED
 */
/** 任务状态（9 态） */
export const TASK_STATUS = {
    /** 草稿：创建后未提交，可自由编辑 */
    DRAFT: 'DRAFT',
    /** 进行中：已提交但尚未进入审批流程（按方案，提交即锁定编辑） */
    IN_PROGRESS: 'IN_PROGRESS',
    /** 已提交：等待进入审批 */
    SUBMITTED: 'SUBMITTED',
    /** 审批中：审批人处理中，提交人不可编辑 */
    APPROVING: 'APPROVING',
    /** 已驳回：退回提交人修改 */
    REJECTED: 'REJECTED',
    /** 已冻结：审批通过，**非管理员不可改** */
    FROZEN: 'FROZEN',
    /** 解冻编辑中：管理员在 72 小时窗口内编辑 */
    UNLOCKED_EDITING: 'UNLOCKED_EDITING',
    /** 已取消 */
    CANCELLED: 'CANCELLED',
    /** 已归档：不再出现在常规视图 */
    ARCHIVED: 'ARCHIVED',
};
/** 全部状态列表（用于校验） */
export const ALL_TASK_STATUSES = Object.values(TASK_STATUS);
export const TASK_STATUS_META = {
    DRAFT: { label: '草稿', tone: 'idle', icon: '○', color: 'var(--idle)' },
    IN_PROGRESS: { label: '进行中', tone: 'run', icon: '▶', color: 'var(--run)' },
    SUBMITTED: { label: '已提交', tone: 'warn', icon: '↑', color: 'var(--warn)' },
    APPROVING: { label: '审批中', tone: 'warn', icon: '⏳', color: 'var(--warn)' },
    REJECTED: { label: '已驳回', tone: 'alert', icon: '✕', color: 'var(--alert)' },
    FROZEN: { label: '已冻结', tone: 'frozen', icon: '🔒', color: 'var(--frozen)' },
    UNLOCKED_EDITING: { label: '解冻编辑中', tone: 'frozen', icon: '🔓', color: 'var(--frozen)' },
    CANCELLED: { label: '已取消', tone: 'idle', icon: '⊘', color: 'var(--idle)' },
    ARCHIVED: { label: '已归档', tone: 'idle', icon: '▣', color: 'var(--idle)' },
};
/** 安全取状态元数据（未知名回退，空值默认草稿） */
export function taskStatusMeta(state) {
    const key = state ?? TASK_STATUS.DRAFT;
    return (TASK_STATUS_META[key] ?? {
        label: key,
        tone: 'idle',
        icon: '?',
        color: 'var(--idle)',
    });
}
/** 仅取中文标签（星图悬浮卡等只需文案的场景） */
export function taskStatusLabel(state) {
    return taskStatusMeta(state).label;
}
/**
 * 合法的状态流转表。
 * 任何未列出的流转都必须被拒绝——这是服务层防线的依据。
 */
export const TASK_STATUS_TRANSITIONS = {
    DRAFT: ['SUBMITTED', 'IN_PROGRESS', 'CANCELLED'],
    IN_PROGRESS: ['SUBMITTED', 'DRAFT', 'CANCELLED'],
    SUBMITTED: ['APPROVING', 'DRAFT', 'CANCELLED'],
    // ⭐ P6.16：SLA 超时（审批实例 EXPIRED）后，允许由有权者**显式终止**，
    //    任务回到 IN_PROGRESS 后可修改并重新提交（不引入任何自动决策）。
    APPROVING: ['FROZEN', 'REJECTED', 'CANCELLED', 'IN_PROGRESS'],
    REJECTED: ['DRAFT', 'SUBMITTED', 'CANCELLED'],
    FROZEN: ['UNLOCKED_EDITING', 'ARCHIVED'],
    UNLOCKED_EDITING: ['FROZEN', 'SUBMITTED', 'APPROVING'],
    CANCELLED: ['DRAFT', 'ARCHIVED'],
    ARCHIVED: [],
};
/** 判断状态流转是否合法 */
export function canTransition(from, to) {
    return TASK_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}
/**
 * 处于「冻结语义」下的状态集合。
 *
 * 这些状态下，**非管理员对业务字段的任何写操作都必须被拒绝**：
 * - 应用层守卫（第一层）
 * - 服务层拦截（第二层）
 * - 数据库触发器（第三层）
 *
 * ⚠️ UNLOCKED_EDITING 虽可编辑，但**仅限管理员**，且必须校验
 *    `unlock_expires_at > now()`——数据库兜底，不依赖 Redis。
 */
export const FROZEN_LIKE_STATUSES = ['FROZEN', 'ARCHIVED'];
/** 该状态是否禁止非管理员编辑 */
export function isLockedForNonAdmin(status) {
    return FROZEN_LIKE_STATUSES.includes(status) || status === 'APPROVING' || status === 'SUBMITTED';
}
//# sourceMappingURL=task-status.js.map